/**
 * Request Account - Submission Confirmation Page
 *
 * Landing page after a successful signup-form submission. Copy is role-aware:
 *
 * - Investors → "We'll review your request" — the API wrote a PENDING
 *   account_requests row, an admin will approve it, and the user will get a
 *   welcome email at that point.
 * - Agents → "Check your email" — the API has already provisioned the user;
 *   they need to click the 72-hour password-setup link.
 */

import { Link } from '@tanstack/react-router'
import { useSearch } from '@tanstack/react-router'
import { Button } from '@/common/components/button'
import { Text } from '@/common/components/text'
import type { RequestedRole } from '@/features/account-request/api/accountRequest'
import * as styles from './index.css'

export const Route = createFileRoute({
  validateSearch: (search: Record<string, unknown>): { role?: RequestedRole } => {
    const role = search.role
    if (role === 'investor' || role === 'agent') {
      return { role }
    }
    return {}
  },
  component: SubmittedPage,
})

function SubmittedPage() {
  // strict:false so unit tests rendering without route context don't crash.
  const search = useSearch({ strict: false }) as { role?: RequestedRole }
  const role: RequestedRole = search.role === 'agent' ? 'agent' : 'investor'

  if (role === 'investor') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.header}>
            <img
              src="/images/orchard-offers.svg"
              alt="Orchard Cash Offer"
              className={styles.logo}
            />
            <Text as="h1" variant="h4" weight="bold" className={styles.title}>
              Request submitted
            </Text>
            <Text variant="body" className={styles.subtitle}>
              Thanks — we'll review your request and get back to you soon.
            </Text>
          </div>

          <Text variant="body" className={styles.description}>
            We sent a confirmation to your inbox. An Orchard team member will
            review your application and email you once your account is ready.
          </Text>

          <div className={styles.infoBox}>
            <Text variant="bodySm" className={styles.infoText}>
              If you don't hear back within a few business days, check your
              spam folder or contact support.
            </Text>
          </div>

          <Link to="/">
            <Button variant="primary" width="full">
              Return to Home
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <img
            src="/images/orchard-offers.svg"
            alt="Orchard Cash Offer"
            className={styles.logo}
          />
          <Text as="h1" variant="h4" weight="bold" className={styles.title}>
            Check your email
          </Text>
          <Text variant="body" className={styles.subtitle}>
            Your account is ready — one more step to log in.
          </Text>
        </div>

        <Text variant="body" className={styles.description}>
          We sent a link to your inbox. Click it to set your password and
          finish signing in.
        </Text>

        <div className={styles.infoBox}>
          <Text variant="bodySm" className={styles.infoText}>
            The link expires in 72 hours. If you don't see the email, check
            your spam folder or contact support.
          </Text>
        </div>

        <Link to="/">
          <Button variant="primary" width="full">
            Return to Home
          </Button>
        </Link>
      </div>
    </div>
  )
}
